/**
 * focusStore (TASK-714) — the popup-workspace stack behind `?focus=`.
 *
 * The dashboard summons full workspaces as modal overlays above the canvas —
 * NEVER by navigating away. The stack holds `{workspace, entityId}` entries;
 * the topmost renders, entries beneath show as chips. Max depth 3: a push at
 * the cap is REJECTED with a visible signal (`depthRejected` bump), never a
 * silent eviction.
 *
 * ## Close is a request, not a command
 * Workspaces can hold unsaved work (KYC form, workbench edits). The topmost
 * workspace may register a close guard; Esc, ✕, Back, and stack eviction all
 * route through `requestClose()`. A veto (guard → false) leaves popup, stack,
 * and `?focus=` untouched. The guard belongs to the topmost popup only and is
 * cleared when it pops.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type FocusWorkspace = 'meeting' | 'kyc' | 'workbench';

export interface FocusEntry {
  workspace: FocusWorkspace;
  entityId: string;
}

export const MAX_FOCUS_DEPTH = 3;

const WORKSPACES: ReadonlySet<string> = new Set(['meeting', 'kyc', 'workbench']);

export const focusParamOf = (entry: FocusEntry): string =>
  `${entry.workspace}:${entry.entityId}`;

export const parseFocusParam = (value: string | null): FocusEntry | null => {
  if (!value) return null;
  const idx = value.indexOf(':');
  if (idx <= 0) return null;
  const workspace = value.slice(0, idx);
  const entityId = value.slice(idx + 1);
  if (!WORKSPACES.has(workspace) || !entityId) return null;
  return { workspace: workspace as FocusWorkspace, entityId };
};

type CloseGuard = () => boolean | Promise<boolean>;

interface FocusState {
  stack: FocusEntry[];
  /** Bumped when a push is rejected at max depth — hosts show a hint. */
  depthRejected: number;
  top: () => FocusEntry | null;
  /** Returns false (and bumps depthRejected) at max depth. */
  push: (entry: FocusEntry) => boolean;
  /**
   * Ask the topmost workspace to close. Runs its guard (default: allow);
   * a veto changes nothing. Resolves true when the popup actually closed.
   */
  requestClose: () => Promise<boolean>;
  /** Register/clear the topmost popup's close guard (null = close freely). */
  registerCloseGuard: (guard: CloseGuard | null) => void;
  /** Close everything, honoring guards top-down; stops at the first veto. */
  closeAll: () => Promise<boolean>;
}

let closeGuard: CloseGuard | null = null;

export const useFocusStore = create<FocusState>()(
  devtools(
    (set, get) => ({
      stack: [],
      depthRejected: 0,

      top: () => {
        const { stack } = get();
        return stack.length > 0 ? stack[stack.length - 1] : null;
      },

      push: (entry: FocusEntry) => {
        const { stack } = get();
        const topEntry = stack[stack.length - 1];
        if (
          topEntry &&
          topEntry.workspace === entry.workspace &&
          topEntry.entityId === entry.entityId
        ) {
          return true; // already focused — no duplicate
        }
        if (stack.length >= MAX_FOCUS_DEPTH) {
          set((s) => ({ depthRejected: s.depthRejected + 1 }));
          return false;
        }
        closeGuard = null; // the new topmost starts unguarded
        set({ stack: [...stack, entry] });
        return true;
      },

      requestClose: async () => {
        const { stack } = get();
        if (stack.length === 0) return true;
        if (closeGuard) {
          let allowed = false;
          try {
            allowed = await closeGuard();
          } catch {
            allowed = false; // a broken guard must not silently discard work
          }
          if (!allowed) return false;
        }
        closeGuard = null;
        set({ stack: get().stack.slice(0, -1) });
        return true;
      },

      registerCloseGuard: (guard: CloseGuard | null) => {
        closeGuard = guard;
      },

      closeAll: async () => {
        while (get().stack.length > 0) {
          const closed = await get().requestClose();
          if (!closed) return false;
        }
        return true;
      },
    }),
    { name: 'FocusStore' }
  )
);
